import { useCallback, useMemo, type Dispatch } from "react";
import type { DisplayMode } from "@karasu-tools/core";
import { nodePathMatchesSuffix, parseNodePathRefId } from "@karasu-tools/core";
import type { AppAction } from "../state/app-reducer.js";

interface UseCrossNavigationArgs {
  dispatch: Dispatch<AppAction>;
  teamPathIndex: Map<string, string[]>;
  orgNodePathIndex: Map<string, string[]>;
  navigateViewPath: (path: string[]) => void;
}

export interface UseCrossNavigationResult {
  /**
   * Click a container in the deploy view → switch to the system view and
   * highlight the node it realizes. `null` when the container realizes no
   * single node (qualified or narrowed, #2818): switch, highlight nothing.
   */
  handleContainerClick: (realizedNodeId: string | null) => void;
  /** Click the deploy-badge button on a service → switch to deploy view with it highlighted. */
  handleDeployButtonClick: (serviceId: string) => void;
  /** Click the team-badge button on a service → switch to org view, navigate to the parent. */
  handleTeamButtonClick: (teamId: string) => void;
  /**
   * Click an "owned by" service in the org view → switch to system view, navigate to it.
   * Takes the button's `nodePathRefId` text, the reference as the author wrote it.
   */
  handleOwnedServiceClick: (ref: string) => void;
  handleDisplayModeChange: (mode: DisplayMode) => void;
  handleDeployBlockChange: (id: string) => void;
  clearHighlight: () => void;
}

/**
 * Thin dispatch wrappers that AppShell passes to child panes for cross-view
 * navigation (e.g. clicking a deploy badge in the system view switches to the
 * deploy view). Kept together here so AppShell stays focused on layout.
 */
export function useCrossNavigation({
  dispatch,
  teamPathIndex,
  orgNodePathIndex,
  navigateViewPath,
}: UseCrossNavigationArgs): UseCrossNavigationResult {
  const handleContainerClick = useCallback(
    (realizedNodeId: string | null) => {
      dispatch({ type: "SET_ACTIVE_VIEW", activeView: "system", highlightNodeId: realizedNodeId });
    },
    [dispatch],
  );

  const handleDeployButtonClick = useCallback(
    (serviceId: string) => {
      // A node id crosses into the deploy view, which marks that node on the
      // container realizing it (#2818).
      dispatch({
        type: "SET_ACTIVE_VIEW",
        activeView: "deploy",
        highlightNodeId: serviceId,
        highlightAttribute: "data-realized-node-id",
      });
    },
    [dispatch],
  );

  const handleTeamButtonClick = useCallback(
    (teamId: string) => {
      const parentPath = teamPathIndex.get(teamId) ?? [];
      dispatch({ type: "SET_ACTIVE_VIEW", activeView: "org", highlightNodeId: teamId });
      if (parentPath.length > 0) navigateViewPath(parentPath);
    },
    [dispatch, teamPathIndex, navigateViewPath],
  );

  const handleOwnedServiceClick = useCallback(
    (ref: string) => {
      // The button carries the reference in `nodePathRefId` form (#2819), so
      // decode it rather than look the text up as an id: `owns Shop.Api` would
      // otherwise land on a top-level `service "Shop.Api"` instead. The node id
      // is the last segment; the indexed path is used only when the reference
      // actually names it (the suffix rule), so a qualified reference whose id
      // resolves elsewhere switches views without navigating to the wrong node.
      const segments = parseNodePathRefId(ref);
      const nodeId = segments[segments.length - 1] ?? ref;
      const indexedPath = orgNodePathIndex.get(nodeId);
      const resolvedPath =
        indexedPath !== undefined && nodePathMatchesSuffix(segments, indexedPath)
          ? indexedPath
          : undefined;
      dispatch({ type: "SET_ACTIVE_VIEW", activeView: "system", highlightNodeId: nodeId });
      if (resolvedPath !== undefined) navigateViewPath(resolvedPath);
    },
    [dispatch, orgNodePathIndex, navigateViewPath],
  );

  const handleDisplayModeChange = useCallback(
    (mode: DisplayMode) => {
      dispatch({ type: "SET_DISPLAY_MODE", displayMode: mode });
    },
    [dispatch],
  );

  const handleDeployBlockChange = useCallback(
    (id: string) => {
      dispatch({ type: "SET_SELECTED_DEPLOY_BLOCK", id });
    },
    [dispatch],
  );

  const clearHighlight = useCallback(() => {
    dispatch({ type: "SET_HIGHLIGHTED_NODE", nodeId: null });
  }, [dispatch]);

  return useMemo(
    () => ({
      handleContainerClick,
      handleDeployButtonClick,
      handleTeamButtonClick,
      handleOwnedServiceClick,
      handleDisplayModeChange,
      handleDeployBlockChange,
      clearHighlight,
    }),
    [
      handleContainerClick,
      handleDeployButtonClick,
      handleTeamButtonClick,
      handleOwnedServiceClick,
      handleDisplayModeChange,
      handleDeployBlockChange,
      clearHighlight,
    ],
  );
}
